import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaClock, FaUsers, FaShieldAlt, FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaArrowRight, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Badge1 from "./assets/Badge/Badge1.webp";
import Badge2 from "./assets/Badge/Badge2.webp";
import Badge3 from "./assets/Badge/Badge3.webp";
import Badge4 from "./assets/Badge/Badge4.webp";
import Badge5 from "./assets/Badge/Badge5.webp";

const VideoHero = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef(null);

  // Badge information data
  const badgeInfo = {
    Badge1: {
      title: "CMA – The Complementary Medical Association",
      description: "The Complementary Medical Association (CMA) is an international professional body that recognises practitioners working in complementary and holistic health. Membership shows a commitment to professional standards, ethics, and ongoing development within alternative healthcare practices.",
      image: Badge1
    },
    Badge2: {
      title: "BICOM",
      description: "BICOM certification relates to the use of bioresonance therapy equipment designed to support wellbeing through frequency-based approaches. Being a certified BICOM practitioner indicates training in using this technology safely and professionally.",
      image: Badge2
    },
    Badge3: {
      title: "NCSCT",
      description: "The National Centre for Smoking Cessation and Training (NCSCT) provides evidence-based training for practitioners helping people stop smoking. Certification demonstrates knowledge of effective behavioural support and smoking cessation strategies.",
      image: Badge3
    },
    Badge4: {
      title: "Bioresonance Leicester",
      description: "This certification recognises specialist training in bioresonance methods used to support holistic health and wellbeing. It reflects experience in applying frequency-based assessment and therapy approaches in practice.",
      image: Badge4
    },
    Badge5: {
      title: "Regumed",
      description: "REGUMED certification relates to professional training in advanced bioresonance technology and therapy systems. It shows that the practitioner has completed recognised instruction in using REGUMED devices responsibly.",
      image: Badge5
    }
  };

  const handleBadgeClick = (badgeKey) => {
    setSelectedBadge(badgeKey);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedBadge(null), 300); // Delay clearing selected badge for smooth exit animation
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, closeModal]);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  }, [isVideoPlaying]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((event) => {
    if (event.target === videoRef.current || event.target.closest('[role="button"]')) {
      switch (event.key) {
        case ' ':
        case 'Enter':
          event.preventDefault();
          togglePlayPause();
          break;
        case 'm':
        case 'M':
          event.preventDefault();
          toggleMute();
          break;
        case 'Escape':
          event.preventDefault();
          if (videoRef.current && !videoRef.current.paused) {
            videoRef.current.pause();
            setIsVideoPlaying(false);
          }
          break;
        default:
          break;
      }
    }
  }, [togglePlayPause, toggleMute]);

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="relative min-h-screen w-full bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      {/* Animated Background Elements - reduced motion/size on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating Geometric Shapes - smaller and subtler on mobile */}
        <motion.div
          animate={{ 
            x: [0, 100, 0],
            y: [0, -50, 0],
            rotate: [0, 180, 360],
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-20 left-20 w-16 h-16 sm:w-24 sm:h-24 md:w-32 md:h-32 border-2 border-gold/20 rounded-full opacity-40 md:opacity-60"
        />
        <motion.div
          animate={{ 
            x: [0, -80, 0],
            y: [0, 60, 0],
            rotate: [360, 180, 0],
            scale: [1, 0.8, 1]
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute top-40 right-20 w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gradient-to-br from-blue/15 to-gold/15 rounded-full blur-sm opacity-50 md:opacity-100"
        />
        <motion.div
          animate={{ 
            x: [0, 60, 0],
            y: [0, -40, 0],
            rotate: [0, 270, 360],
            scale: [1, 1.3, 1]
          }}
          transition={{ 
            duration: 30, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          className="absolute bottom-40 left-32 w-10 h-10 sm:w-16 sm:h-16 md:w-20 md:h-20 border border-gold/30 rounded-full opacity-30 md:opacity-40 hidden sm:block"
        />
        
        {/* Particle Effects - fewer on mobile */}
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-gold rounded-full animate-pulse opacity-50 md:opacity-70"></div>
        <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-blue rounded-full animate-pulse opacity-30 md:opacity-50"></div>
        <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-gold rounded-full animate-pulse opacity-40 md:opacity-60 hidden sm:block"></div>
        <div className="absolute bottom-1/4 right-1/4 w-4 h-4 bg-blue rounded-full animate-pulse opacity-30 md:opacity-40"></div>
      </div>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 md:py-20 overflow-x-hidden overflow-y-visible">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-3 sm:px-4 relative z-10 pt-20 sm:pt-24 md:pt-32">
          <div className="flex flex-col gap-8 md:gap-12 max-w-7xl mx-auto">
            {/* Enhanced Creative Video Section - Full Width */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative -mt-8 md:-mt-20"
            >
              <div className="relative group">
                {/* Enhanced Decorative Background Shapes - hidden on mobile to reduce clutter */}
                <div className="hidden md:block absolute -top-8 -right-8 w-40 h-40 bg-gradient-to-br from-gold/25 to-yellow/25 rounded-full opacity-70 group-hover:scale-110 transition-transform duration-700 blur-md z-0"></div>
                <div className="hidden md:block absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-br from-blue/35 to-purple/25 rounded-full opacity-60 group-hover:scale-125 transition-transform duration-800 blur-md z-0"></div>
                <div className="hidden md:block absolute top-1/2 -right-4 w-20 h-20 bg-gradient-to-br from-green/25 to-emerald/25 rounded-full opacity-50 group-hover:scale-110 transition-transform duration-600 blur-md z-0"></div>
                <div className="hidden md:block absolute top-1/4 -left-4 w-16 h-16 bg-gradient-to-br from-pink/20 to-rose/20 rounded-full opacity-40 group-hover:scale-110 transition-transform duration-500 blur-sm z-0"></div>
                
                 {/* Enhanced Main Video Container - responsive: full width on mobile, 120% bleed on md+ */}
                 <div className="relative z-20 w-full md:w-[120%] md:mx-auto md:left-1/2 md:-translate-x-1/2 md:max-w-[calc(100vw+20%)]">
                   <div className="relative overflow-hidden rounded-xl sm:rounded-2xl md:rounded-[2rem] bg-black aspect-video max-h-[45vh] sm:max-h-[50vh] md:max-h-none md:aspect-auto md:h-[38.4rem] w-full shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] group-hover:shadow-[0_35px_60px_-12px_rgba(0,0,0,0.35)] transition-all duration-500">
                    <video
                      ref={videoRef}
                      className="absolute inset-0 w-full h-full object-cover object-center object-top relative z-10"
                      autoPlay
                      muted={isMuted}
                      loop
                      playsInline
                      onLoadedData={handleVideoLoad}
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                      style={{
                        pointerEvents: 'none'
                      }}
                      role="img"
                      aria-label="Bioresonance therapy demonstration video"
                      tabIndex={-1}
                    >
                      <source src="/Untitled video (5).mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                    
                    {/* Enhanced Video Controls Overlay - smaller on mobile */}
                    <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-50 flex gap-2 sm:gap-4">
                      <button
                        onClick={togglePlayPause}
                        className="bg-white/50 backdrop-blur-lg hover:bg-white/70 text-white transition-all duration-300 hover:scale-110 p-2.5 sm:p-4 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-opacity-50 rounded-full border border-white/70 shadow-xl cursor-pointer z-50"
                        aria-label={isVideoPlaying ? "Pause video" : "Play video"}
                        type="button"
                      >
                        {isVideoPlaying ? (
                          <FaPause className="w-4 h-4 sm:w-6 sm:h-6" />
                        ) : (
                          <FaPlay className="w-4 h-4 sm:w-6 sm:h-6" />
                        )}
                      </button>

                      <button
                        onClick={toggleMute}
                        className="bg-white/50 backdrop-blur-lg hover:bg-white/70 text-white transition-all duration-300 hover:scale-110 p-2.5 sm:p-4 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-opacity-50 rounded-full border border-white/70 shadow-xl cursor-pointer z-50"
                        aria-label={isMuted ? "Unmute video" : "Mute video"}
                        type="button"
                      >
                        {isMuted ? (
                          <FaVolumeMute className="w-4 h-4 sm:w-6 sm:h-6" />
                        ) : (
                          <FaVolumeUp className="w-4 h-4 sm:w-6 sm:h-6" />
                        )}
                      </button>
                    </div>

                    {/* Enhanced Video Title Overlay - hidden on mobile */}
                    <div className="hidden md:block absolute top-8 left-8 z-30">
                      <div className="bg-white/25 backdrop-blur-lg rounded-xl px-6 py-3 border border-white/40 shadow-xl">
                        <p className="text-white text-base font-bold">TP WELLNESS</p>
                        <p className="text-white/90 text-sm">Live Demonstration</p>
                      </div>
                    </div>

                    {/* Video Progress Indicator */}
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 z-30">
                      <div className="h-full bg-gradient-to-r from-gold to-yellow w-0 group-hover:w-full transition-all duration-1000 ease-out"></div>
                    </div>

                    {/* Enhanced Loading State Overlay */}
                    {!isVideoLoaded && (
                      <div className="absolute inset-0 bg-gradient-to-br from-gold/30 via-blue/30 to-green/30 flex items-center justify-center z-40">
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-center"
                        >
                          <div className="w-20 h-20 bg-white/30 backdrop-blur-lg rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                            <FaHeart className="text-3xl text-white animate-pulse" />
                          </div>
                          <div className="text-white text-xl font-bold">Loading Experience...</div>
                          <div className="text-white/80 text-sm mt-2">Preparing your wellness journey</div>
                        </motion.div>
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced Creative Borders - desktop only so they match fixed video height */}
                  <div className="hidden md:block absolute top-0 left-0 right-0 h-[38.4rem] border-4 border-gold/50 rounded-[2rem] transform rotate-3 group-hover:rotate-0 transition-transform duration-700 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]"></div>
                  <div className="hidden md:block absolute top-4 left-4 right-4 h-[36.3rem] border-2 border-white/70 rounded-[2rem] transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="hidden md:block absolute top-8 left-8 right-8 h-[35.8rem] border border-gold/30 rounded-[2rem] transform rotate-1 group-hover:rotate-0 transition-transform duration-300"></div>
                  <div className="hidden md:block absolute top-12 left-12 right-12 h-[35.3rem] border border-white/20 rounded-[2rem] transform -rotate-1 group-hover:rotate-0 transition-transform duration-200"></div>
                </div>
                
                 {/* Enhanced Floating Elements */}
                 <motion.div 
                   animate={{ 
                     y: [0, -20, 0],
                     scale: [1, 1.2, 1],
                     opacity: [0.7, 1, 0.7]
                   }}
                   transition={{ 
                     duration: 3, 
                     repeat: Infinity, 
                     ease: "easeInOut" 
                   }}
                   className="absolute top-16 right-16 w-5 h-5 bg-gradient-to-r from-gold to-yellow rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 shadow-xl z-10"
                 ></motion.div>
                 <motion.div 
                   animate={{ 
                     y: [0, 15, 0],
                     scale: [1, 0.8, 1],
                     opacity: [0.5, 1, 0.5]
                   }}
                   transition={{ 
                     duration: 4, 
                     repeat: Infinity, 
                     ease: "easeInOut",
                     delay: 0.5
                   }}
                   className="absolute bottom-16 left-16 w-4 h-4 bg-gradient-to-r from-blue to-purple rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 shadow-xl z-10"
                 ></motion.div>
                 <motion.div 
                   animate={{ 
                     x: [0, 10, 0],
                     y: [0, -10, 0],
                     rotate: [0, 180, 360]
                   }}
                   transition={{ 
                     duration: 5, 
                     repeat: Infinity, 
                     ease: "easeInOut",
                     delay: 1
                   }}
                   className="absolute top-1/3 right-12 w-3 h-3 bg-gradient-to-r from-green to-emerald rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300 shadow-xl z-10"
                 ></motion.div>
                 <motion.div 
                   animate={{ 
                     x: [0, -15, 0],
                     y: [0, 20, 0],
                     scale: [1, 1.3, 1]
                   }}
                   transition={{ 
                     duration: 6, 
                     repeat: Infinity, 
                     ease: "easeInOut",
                     delay: 1.5
                   }}
                   className="absolute bottom-1/3 left-12 w-3 h-3 bg-gradient-to-r from-pink to-rose rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-400 shadow-xl z-10"
                 ></motion.div>
                
                {/* Enhanced Decorative Elements */}
                <div className="absolute -top-4 -left-4 w-12 h-12 border-3 border-gold/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-500 shadow-lg z-10"></div>
                <div className="absolute -bottom-4 -right-4 w-10 h-10 border-3 border-blue/40 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-600 shadow-lg z-10"></div>
                <div className="absolute top-1/2 -left-6 w-8 h-8 border-2 border-green/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-700 shadow-md z-10"></div>
                <div className="absolute bottom-1/2 -right-6 w-6 h-6 border-2 border-pink/30 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-800 shadow-md z-10"></div>
                
                {/* Corner Accent Elements */}
                <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-gold/20 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-900 z-10"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-blue/20 to-transparent rounded-tr-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-1000 z-10"></div>
              </div>
            </motion.div>

            {/* Text Content Section - centered, badges as trust strip under title */}
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6 text-center pt-4"
            >
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4 mx-auto"
              >
                <FaHeart className="mr-2" />
                Holistic Wellness Center
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="text-5xl md:text-6xl font-bold leading-tight relative" 
                style={{ fontFamily: "Playfair Display, serif" }}
              >
                <span className="bg-gradient-to-r from-[#D4AF6A] via-gold to-[#7A5C1E] bg-clip-text text-transparent">
                  TP WELLNESS
                </span>
              </motion.h1>

              {/* Trust strip: badges in a row under the title */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="flex flex-wrap items-center justify-center gap-3 py-2 md:gap-10 md:py-4"
              >
                <img
                  src={Badge1}
                  alt="CMA – The Complementary Medical Association"
                  onClick={() => handleBadgeClick('Badge1')}
                  className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBadgeClick('Badge1');
                    }
                  }}
                />
                <img
                  src={Badge2}
                  alt="BICOM Certification"
                  onClick={() => handleBadgeClick('Badge2')}
                  className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBadgeClick('Badge2');
                    }
                  }}
                />
                <img
                  src={Badge3}
                  alt="NCSCT Certification"
                  onClick={() => handleBadgeClick('Badge3')}
                  className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBadgeClick('Badge3');
                    }
                  }}
                />
                <img
                  src={Badge4}
                  alt="Certified Practitioner Bioresonance Leicester"
                  onClick={() => handleBadgeClick('Badge4')}
                  className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBadgeClick('Badge4');
                    }
                  }}
                />
                <img
                  src={Badge5}
                  alt="REGUMED Certification"
                  onClick={() => handleBadgeClick('Badge5')}
                  className="h-24 md:h-28 lg:h-32 w-auto object-contain drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleBadgeClick('Badge5');
                    }
                  }}
                />
              </motion.div>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-xl text-dark-grey leading-relaxed"
              >
                Supporting Your Journey to Holistic Wellness
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto"
              >
                We can support holistic therapy of many diseases, painful conditions, 
                allergies and addictions through advanced BICOM bioresonance technology and 
                personalized care approaches.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap gap-4 pt-4 justify-center"
              >
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>Flexible scheduling</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages welcome</span>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="flex flex-col gap-6 pt-6 justify-center items-center w-full"
              >
                {/* Primary CTA - Book Your Wellness Assessment - PROMINENT */}
                <motion.div
                  className="w-full max-w-xl mx-auto relative"
                  animate={{
                    scale: [1, 1.02, 1],
                    y: [0, -2, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  {/* Outer glow ring */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-gold via-yellow to-gold rounded-full blur-lg opacity-70 animate-pulse"></div>
                  
                  <a
                    href="https://www.sumupbookings.com/t-p-wellness-ltd"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative w-full bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-6 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center justify-center text-lg md:text-xl overflow-hidden group border-2 border-gold/30 shadow-xl"
                    style={{
                      textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                      boxShadow: '0 0 30px rgba(212, 175, 106, 0.6), 0 10px 40px rgba(212, 175, 106, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                    }}
                  >
                    {/* Shimmer effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"></div>
                    
                    {/* Animated background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow via-gold to-yellow opacity-100"></div>
                    
                    <span className="relative z-10 flex items-center gap-2 font-bold tracking-wide">
                      <span className="text-xl">✨</span>
                      <span>Book Your Wellness Assessment</span>
                      <FaArrowRight className="text-xl group-hover:translate-x-2 transition-transform duration-300" />
                    </span>
                    
                    {/* Sparkle effects */}
                    <div className="absolute top-1 left-3 w-1.5 h-1.5 bg-gold rounded-full opacity-90 animate-ping"></div>
                    <div className="absolute bottom-1 right-3 w-1.5 h-1.5 bg-yellow rounded-full opacity-90 animate-ping" style={{ animationDelay: '0.5s' }}></div>
                  </a>
                </motion.div>
                
                {/* Secondary buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full">
                  <Link
                    to="/therapy"
                    className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg flex items-center justify-center"
                  >
                    <span>Explore Our Services</span>
                    <FaArrowRight className="ml-2" />
                  </Link>
                  <a
                    href="#treatment-services"
                    className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300 flex items-center justify-center"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('treatment-services');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                  >
                    Our Services
                  </a>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Badge Information Modal */}
      <AnimatePresence>
        {isModalOpen && selectedBadge && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
              onClick={closeModal}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border-4 border-gold/30">
                {/* Decorative background elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gold/10 to-transparent rounded-bl-full"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue/10 to-transparent rounded-tr-full"></div>
                
                {/* Close button */}
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 z-10 bg-white/90 hover:bg-gold text-gray-700 hover:text-white rounded-full p-2 transition-all duration-300 hover:scale-110 shadow-lg"
                  aria-label="Close modal"
                >
                  <FaTimes className="w-5 h-5" />
                </button>

                {/* Modal content */}
                <div className="relative p-6 md:p-8 overflow-y-auto max-h-[90vh]">
                  {/* Badge image */}
                  <div className="flex justify-center mb-6">
                    <div className="relative group">
                      <img
                        src={badgeInfo[selectedBadge].image}
                        alt={badgeInfo[selectedBadge].title}
                        className="h-32 md:h-40 w-auto object-contain drop-shadow-lg"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-blue/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 
                    className="text-2xl md:text-3xl font-bold text-dark-grey mb-4 text-center"
                    style={{ fontFamily: "Playfair Display, serif" }}
                  >
                    {badgeInfo[selectedBadge].title}
                  </h2>

                  {/* Divider */}
                  <div className="flex items-center justify-center mb-6">
                    <div className="h-px bg-gradient-to-r from-transparent via-gold to-transparent w-full max-w-xs"></div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 leading-relaxed text-base md:text-lg text-center">
                    {badgeInfo[selectedBadge].description}
                  </p>

                  {/* Decorative elements */}
                  <div className="mt-8 flex justify-center gap-2">
                    <div className="w-2 h-2 bg-gold rounded-full"></div>
                    <div className="w-2 h-2 bg-blue rounded-full"></div>
                    <div className="w-2 h-2 bg-gold rounded-full"></div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VideoHero;
